import { inject, injectable } from 'inversify';
import { Result } from '@Redux/type';
import { TYPES } from '@Types';
import {
  RequestLocationSuccess,
  RequestUserPreferenceSuccess,
  UserPreferenceRepository,
} from '@Repository/user';
import { UserPreference } from '@Models';
import { ICrudEntityAction } from '@Redux/ICrudEntityAction';
import userPreferenceSlice from '@Redux/reducers/user/user-preference.slice';
import { PayloadAction } from '@reduxjs/toolkit';

@injectable()
export class UserPreferenceAction extends ICrudEntityAction<UserPreference> {
  @inject(TYPES.Repository.UserPreferenceRepository)
  private readonly _userPreferenceRepository!: UserPreferenceRepository;

  public fetchData = (): Result<void> => async (dispatch) => {
    dispatch(this.areLoading(true));

    const cachedItem = await this._userPreferenceRepository.getCached();
    if (cachedItem != null) {
      dispatch(this.cacheDataSuccess(cachedItem));
    }

    const response = await this._userPreferenceRepository.get();

    if (this.handleFailure(response, dispatch)) return;

    const success = response as RequestUserPreferenceSuccess;
    dispatch(this.fetchDataSuccess(success.payload));
    dispatch(this.areLoading(false));
  };

  protected fetchDataSuccess(
    item: UserPreference,
  ): PayloadAction<UserPreference> {
    return userPreferenceSlice.actions.setUserPreference(item);
  }

  protected cacheDataSuccess(
    item: UserPreference,
  ): PayloadAction<UserPreference> {
    return userPreferenceSlice.actions.setUserPreference(item);
  }

  protected haveError(message: string): PayloadAction<string> {
    return userPreferenceSlice.actions.setError(message);
  }

  protected areLoading(bool: boolean): PayloadAction<boolean> {
    return userPreferenceSlice.actions.setLoading(bool);
  }

  public saveLocation =
    (playerId: string, location: google.maps.LatLngLiteral): Result<void> =>
    async (dispatch, getState) => {
      dispatch(this.areLoading(true));

      const response = await this._userPreferenceRepository.saveLocation(
        playerId,
        location,
      );

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestLocationSuccess;

      const state = getState();
      const userPreference = state.userPreference.entity;

      dispatch(
        this.fetchDataSuccess({
          ...userPreference!,
          location: success.payload,
        }),
      );
      dispatch(this.areLoading(false));
    };

  public deleteLocation =
    (playerId: string): Result<void> =>
    async (dispatch, getState) => {
      dispatch(this.areLoading(true));

      const response =
        await this._userPreferenceRepository.deleteLocation(playerId);

      if (this.handleFailure(response, dispatch)) return;

      const state = getState();
      const userPreference = state.userPreference.entity;

      dispatch(this.fetchDataSuccess({ ...userPreference!, location: null }));
      dispatch(this.areLoading(false));
    };
}
